import { ref, onMounted, onUnmounted, watch } from "vue"

/**
 * Composable for handling intersection observer functionality
 *
 * @param {Object} options - Configuration options for the intersection observer
 * @param {Function} options.onIntersect - Callback function triggered when target element intersects
 * @param {Object} options.observerOptions - IntersectionObserver options
 * @param {HTMLElement|null} options.observerOptions.root - The element that is used as the viewport
 * @param {string} options.observerOptions.rootMargin - Margin around the root
 * @param {number|number[]} options.observerOptions.threshold - Percentage of target's visibility the observer's callback should be executed
 *
 * @returns {Object} - Object containing the target element reference and utility functions
 */
export default function useIntersectionObserver(options = {}) {
  const {
    onIntersect = () => {
      /* Default no-op implementation */
    },
    observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1
    }
  } = options

  const targetRef = ref(null)
  const observer = ref(null)
  const isIntersecting = ref(false)

  /**
   * Cleans up the intersection observer
   */
  const cleanup = () => {
    if (observer.value) {
      observer.value.disconnect()
      observer.value = null
    }
  }

  /**
   * Sets up the intersection observer
   */
  const setupObserver = () => {
    if (!targetRef.value || !("IntersectionObserver" in window)) {
      return
    }

    // Clean up any existing observer if any
    cleanup()

    observer.value = new IntersectionObserver((entries) => {
      const [entry] = entries
      isIntersecting.value = entry.isIntersecting

      if (entry.isIntersecting) {
        onIntersect(entry)
      }
    }, observerOptions)

    // observing
    observer.value.observe(targetRef.value)
  }

  watch(
    () => targetRef.value,
    (newValue) => {
      if (newValue) {
        setupObserver()
      }
    }
  )

  // Setup on mount
  onMounted(() => {
    setupObserver()
  })

  // Cleanup on unmount
  onUnmounted(() => {
    cleanup()
  })

  return {
    targetRef,
    isIntersecting,
    setupObserver,
    cleanup
  }
}
